import { useNavigate } from 'react-router-dom';
import { usePublicContent } from '../hooks/usePublicContent';

export const FundacionPage = () => {
  const navigate = useNavigate();
  const { getText, getJson } = usePublicContent('FUNDACION');

  const headerTitle = getText('header', 'titulo', 'Fundación Cuadrangular');
  const headerSubtitle = getText('header', 'subtitulo', 'Transformando vidas y comunidades a través del amor de Cristo y la acción social');
  const verse = getText('header', 'versiculo', 'Porque tuve hambre, y me disteis de comer; tuve sed, y me disteis de beber; fui forastero, y me recogisteis');
  const verseReference = getText('header', 'referencia', '- Mateo 25:35');

  const programasLabel = getText('programas', 'label', 'Nuestra labor');
  const programasTitle = getText('programas', 'titulo', 'Nuestros Programas');
  const programas = getJson<{ titulo: string; descripcion: string; icono: string; detalles: string[] }[]>('programas', 'items', [
    {
      titulo: "Educación para Todos",
      descripcion: "Becas educativas y apoyo escolar para niños y jóvenes de bajos recursos",
      icono: "🎓",
      detalles: [
        "Becas estudiantiles completas",
        "Útiles escolares y uniformes",
        "Apoyo pedagógico personalizado"
      ]
    },
    {
      titulo: "Alimentación Solidaria",
      descripcion: "Programas de alimentación para familias vulnerables",
      icono: "🍞",
      detalles: [
        "Comedores comunitarios",
        "Mercados solidarios",
        "Nutrición infantil especializada"
      ]
    },
    {
      titulo: "Salud Integral",
      descripcion: "Atención médica básica y programas de prevención",
      icono: "🏥",
      detalles: [
        "Consultas médicas gratuitas",
        "Jornadas de vacunación",
        "Programas de salud mental"
      ]
    },
    {
      titulo: "Vivienda Digna",
      descripcion: "Construcción y mejoramiento de viviendas familiares",
      icono: "🏠",
      detalles: [
        "Construcción de viviendas nuevas",
        "Mejoramiento de infraestructura",
        "Acceso a servicios básicos"
      ]
    }
  ]);

  const impactoLabel = getText('impacto', 'label', 'Nuestro alcance');
  const impactoTitle = getText('impacto', 'titulo', 'Impacto Social');
  const impactoSocial = getJson<{ numero: string; descripcion: string; icono: string }[]>('impacto', 'items', [
    { numero: "500+", descripcion: "Familias beneficiadas", icono: "👨‍👩‍👧‍👦" },
    { numero: "1,200+", descripcion: "Niños en programas educativos", icono: "📚" },
    { numero: "800+", descripcion: "Consultas médicas realizadas", icono: "⚕️" },
    { numero: "150+", descripcion: "Viviendas construidas/mejoradas", icono: "🔨" }
  ]);

  const testimoniosLabel = getText('testimonios', 'label', 'Historias de vida');
  const testimoniosTitle = getText('testimonios', 'titulo', 'Testimonios');
  const testimonios = getJson<{ nombre: string; testimonio: string; programa: string }[]>('testimonios', 'items', [
    {
      nombre: "María González",
      testimonio: "Gracias a la fundación, mis hijos pudieron continuar estudiando. Ahora mi hijo mayor está en la universidad.",
      programa: "Educación para Todos"
    },
    {
      nombre: "Carlos Ramírez",
      testimonio: "Reconstruyeron nuestra casa después de las inundaciones. Ahora tenemos un hogar digno y seguro.",
      programa: "Vivienda Digna"
    },
    {
      nombre: "Ana López",
      testimonio: "El programa de alimentación ha sido fundamental para mi familia en estos tiempos difíciles.",
      programa: "Alimentación Solidaria"
    }
  ]);

  const ayudarTitle = getText('ayudar', 'titulo', '¿Cómo Puedes Ayudar?');
  const ayudarDescription = getText('ayudar', 'descripcion', 'Únete a nuestra misión de transformar vidas en la comunidad');
  const ayudarCards = getJson<{ icono: string; titulo: string; descripcion: string }[]>('ayudar', 'cards', [
    {
      icono: "💰",
      titulo: "Donaciones",
      descripcion: "Contribuye económicamente a nuestros programas sociales"
    },
    {
      icono: "🤝",
      titulo: "Voluntariado",
      descripcion: "Dedica tu tiempo y talentos para servir a otros"
    },
    {
      icono: "📢",
      titulo: "Difusión",
      descripcion: "Comparte nuestra misión en tus redes sociales"
    }
  ]);
  const donateButton = getText('ayudar', 'boton_donar', 'Hacer Donación');
  const volunteerButton = getText('ayudar', 'boton_voluntario', 'Ser Voluntario');

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      'bg-gradient-to-br from-gray-50 via-white to-gray-100'
    }`}>
      
      {/* Header */}
      <section className="relative overflow-hidden pt-32 pb-20 px-6">
        <div className="absolute inset-0 church-soft-gradient"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in church-text-gradient">
            {headerTitle}
          </h1>
          <p className="text-xl text-gray-600 animate-slide-up delay-200 max-w-3xl mx-auto">
            {headerSubtitle}
          </p>
          
          <div className="mt-8 p-6 bg-white/80 rounded-2xl backdrop-blur-sm max-w-2xl mx-auto">
            <blockquote className="text-lg italic text-gray-700 mb-4">
              "{verse}"
            </blockquote>
            <cite className="text-red-600 font-semibold">{verseReference}</cite>
          </div>
        </div>
      </section>

      {/* Programas de la Fundación */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-red-600 text-sm uppercase tracking-[0.3em] mb-3 font-medium">{programasLabel}</p>
          <h2 className="text-4xl font-bold text-center mb-4 church-text-gradient">
            {programasTitle}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-red-600 to-blue-600 mx-auto mb-16 rounded-full"></div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {programas.map((programa, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 animate-slide-up"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shrink-0 shadow-md">
                    <span className="text-white text-3xl">{programa.icono}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-800 mb-3">{programa.titulo}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{programa.descripcion}</p>
                    <ul className="space-y-2">
                      {programa.detalles.map((detalle, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                          {detalle}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impacto Social */}
      <section className="py-20 px-6 bg-gradient-to-r from-red-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-white/80 text-sm uppercase tracking-[0.3em] mb-3 font-medium">{impactoLabel}</p>
          <h2 className="text-4xl font-bold text-center mb-16">
            {impactoTitle}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactoSocial.map((item, index) => (
              <div 
                key={index}
                className="text-center p-6 bg-white/10 rounded-2xl backdrop-blur-sm animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="text-5xl mb-4">{item.icono}</div>
                <div className="text-4xl font-bold mb-2">{item.numero}</div>
                <div className="text-white/90">{item.descripcion}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonios */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-red-600 text-sm uppercase tracking-[0.3em] mb-3 font-medium">{testimoniosLabel}</p>
          <h2 className="text-4xl font-bold text-center mb-16 church-text-gradient">
            {testimoniosTitle}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonios.map((testimonio, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 animate-slide-up"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="text-red-500 text-4xl mb-4">"</div>
                <p className="text-gray-600 italic mb-6 leading-relaxed">
                  {testimonio.testimonio}
                </p>
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-bold text-gray-800">
                    {testimonio.nombre}
                  </h4>
                  <p className="text-sm text-red-600 font-medium">
                    {testimonio.programa}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cómo Ayudar */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-red-600/10 to-blue-600/10 rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold mb-6 church-text-gradient">
              {ayudarTitle}
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              {ayudarDescription}
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {ayudarCards.map((card, index) => (
                <div key={index} className="bg-white/80 rounded-xl p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-xl">{card.icono}</span>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">{card.titulo}</h3>
                  <p className="text-sm text-gray-600">
                    {card.descripcion}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/donaciones')}
                className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-500 hover:to-purple-800 transition-all duration-300 transform hover:scale-105"
              >
                {donateButton}
              </button>
              <button className="bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-4 rounded-xl font-semibold hover:from-green-500 hover:to-green-800 transition-all duration-300 transform hover:scale-105">
                {volunteerButton}
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default FundacionPage;
